import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

// Drops trailing zeros while keeping up to 2 decimals.
// 24.60 -> "24.6", 6.89 -> "6.89", 25.00 -> "25", 84.53 -> "84.5" if 1 dp requested.
function _trim(n, maxFrac = 2) {
    if (!Number.isFinite(n)) return '0';
    const fixed = n.toFixed(maxFrac);
    return fixed.replace(/\.?0+$/, '');
}

/**
 * Currency formatter.
 *   formatCurrency(688671390)            -> "₹6.89 Cr"        (compact, KPI default)
 *   formatCurrency(2460000)              -> "₹24.6 L"
 *   formatCurrency(84532)                -> "₹84.5 K"
 *   formatCurrency(688671390, 'INR', { precise: true })
 *       -> "₹68,86,71,390.00"  (for tooltips, tables, invoices, exports)
 */
export function formatCurrency(amount, currency = 'INR', opts = {}) {
    const { precise = false } = opts;
    const num = amount || 0;

    if (currency === 'INR') {
        if (precise) {
            return '₹' + num.toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        }
        const abs = Math.abs(num);
        // Cr keeps 2 decimals to preserve precision on large amounts, L/K use 1.
        if (abs >= 10000000)  return '₹' + _trim(num / 10000000, 2) + ' Cr';
        if (abs >= 100000)    return '₹' + _trim(num / 100000,   1) + ' L';
        if (abs >= 1000)      return '₹' + _trim(num / 1000,     1) + ' K';
        return '₹' + num.toLocaleString('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
    }

    // USD
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
}

// Convenience helper for tooltips / tables / invoices / exports — always full precision.
export function formatCurrencyFull(amount, currency = 'INR') {
    return formatCurrency(amount, currency, { precise: true });
}

export function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000000000) {
        // Billions - show with 2 decimal points
        return (num / 1000000000).toFixed(2) + 'B';
    }
    if (num >= 1000000) {
        // Millions - show with 2 decimal points
        return (num / 1000000).toFixed(2) + 'M';
    }
    if (num >= 1000) {
        // Thousands - show with 2 decimal points
        return (num / 1000).toFixed(2) + 'K';
    }
    return num.toLocaleString();
}

export function formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

export function formatMonth(monthString) {
    if (!monthString) return '';
    
    // Check if it's a standard YYYY-MM format
    const standardMatch = monthString.match(/^(\d{4})-(\d{1,2})$/);
    if (standardMatch) {
        const [, year, month] = standardMatch;
        const date = new Date(year, parseInt(month) - 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    }
    
    // Return original string for non-standard formats (e.g., "FY 24-2")
    return monthString;
}

// Check if a month string is a valid YYYY-MM format
export function isValidMonth(monthString) {
    if (!monthString) return false;
    return /^\d{4}-\d{1,2}$/.test(monthString);
}
